/**
 * Frontend product taxonomy helpers.
 * Keep semantics in sync with functions/products/productTaxonomyFields.js
 */

import { computeEffectiveState } from '../components/CatalogTaxonomy/catalogTaxonomyEffectiveState'
import { isCatalogTaxonomyLeafNode } from '../components/CatalogTaxonomy/catalogTaxonomyLeaf'
import { readProductTaxonomyFields } from './readProductTaxonomyFields'

function getNode(nodesById, id) {
  if (!nodesById || id == null) return null
  if (typeof nodesById.get === 'function') return nodesById.get(id) || null
  return nodesById[id] || null
}

export function uniqueSortedIds(ids = []) {
  const set = new Set()
  ;(Array.isArray(ids) ? ids : []).forEach((id) => {
    if (typeof id === 'string' && id.length > 0) set.add(id)
  })
  return Array.from(set).sort()
}

export function buildCatalogTaxonomyMembershipIds(assignmentIds, nodesById) {
  const membership = new Set()

  uniqueSortedIds(assignmentIds).forEach((assignmentId) => {
    let currentId = assignmentId
    const seen = new Set()
    for (let depth = 0; depth < 20; depth += 1) {
      if (!currentId || seen.has(currentId)) break
      seen.add(currentId)
      membership.add(currentId)
      const node = getNode(nodesById, currentId)
      if (!node) break
      currentId = node.parentId || null
    }
  })

  return Array.from(membership).sort()
}

export function normalizeProductTaxonomyAssignments({
  assignmentIds = [],
  previousCanonical = null,
} = {}) {
  const nextAssignments = uniqueSortedIds(assignmentIds)

  if (nextAssignments.length === 0) {
    return {
      catalogTaxonomyAssignmentIds: [],
      canonicalCatalogTaxonomyNodeId: null,
    }
  }

  if (nextAssignments.length === 1) {
    return {
      catalogTaxonomyAssignmentIds: nextAssignments,
      canonicalCatalogTaxonomyNodeId: nextAssignments[0],
    }
  }

  const previous =
    typeof previousCanonical === 'string' && previousCanonical.length > 0
      ? previousCanonical
      : null

  if (previous && nextAssignments.includes(previous)) {
    return {
      catalogTaxonomyAssignmentIds: nextAssignments,
      canonicalCatalogTaxonomyNodeId: previous,
    }
  }

  return {
    catalogTaxonomyAssignmentIds: nextAssignments,
    canonicalCatalogTaxonomyNodeId: null,
  }
}

/** Ancestors root → parent (excluding self). */
export function resolveAncestors(nodeId, nodesById) {
  const ancestors = []
  const seen = new Set()
  let current = getNode(nodesById, nodeId)
  let parentId = current?.parentId || null

  for (let i = 0; i < 20; i += 1) {
    if (!parentId || seen.has(parentId)) break
    seen.add(parentId)
    const parent = getNode(nodesById, parentId)
    if (!parent) break
    ancestors.unshift(parent)
    parentId = parent.parentId || null
  }
  return ancestors
}

export function assertCanAssignTaxonomyNode(node, ancestors = [], nodesById = null) {
  if (!node) {
    return { allowed: false, code: 'missing_node', message: 'Вузол не знайдено.', warning: null }
  }
  if (nodesById && !isCatalogTaxonomyLeafNode(node.id, nodesById)) {
    return {
      allowed: false,
      code: 'non_leaf',
      message: 'Товар можна призначити лише до кінцевої категорії',
      warning: null,
    }
  }
  const { effectiveArchived } = computeEffectiveState(node, ancestors)
  if (effectiveArchived || node.isArchived === true) {
    return {
      allowed: false,
      code: 'archived_branch',
      message: 'Не можна призначити архівовану гілку (вузол або предок в архіві).',
      warning: null,
    }
  }
  if (node.isActive !== true || ancestors.some((a) => a?.isActive !== true)) {
    return {
      allowed: true,
      code: 'inactive_branch',
      message: null,
      warning: 'Гілка неактивна: призначення дозволене, але категорія може бути недоступна на сайті.',
    }
  }
  return { allowed: true, code: 'ok', message: null, warning: null }
}

export function assertProductActivationReadiness(product, nodesById) {
  const taxonomy = readProductTaxonomyFields(product)
  const blockers = []
  const warnings = []

  if (taxonomy.catalogTaxonomyAssignmentIds.length === 0) {
    blockers.push({
      code: 'empty_assignments',
      message: 'Потрібно обрати основну категорію',
    })
  }

  taxonomy.catalogTaxonomyAssignmentIds.forEach((id) => {
    if (!isCatalogTaxonomyLeafNode(id, nodesById)) {
      blockers.push({
        code: 'non_leaf_assignment',
        message: 'Товар можна призначити лише до кінцевої категорії',
      })
    }
  })

  const canonical = taxonomy.canonicalCatalogTaxonomyNodeId
  if (canonical == null) {
    blockers.push({
      code: 'canonical_required',
      message: 'Потрібно обрати основну категорію',
    })
  } else if (!taxonomy.catalogTaxonomyAssignmentIds.includes(canonical)) {
    blockers.push({
      code: 'canonical_not_in_assignments',
      message: 'Основна категорія має бути серед категорій товару',
    })
  } else if (!isCatalogTaxonomyLeafNode(canonical, nodesById)) {
    blockers.push({
      code: 'canonical_not_leaf',
      message: 'Товар можна призначити лише до кінцевої категорії',
    })
  } else {
    const node = getNode(nodesById, canonical)
    if (!node) {
      blockers.push({
        code: 'canonical_missing',
        message: 'Основна категорія недоступна або неактивна',
      })
    } else {
      const ancestors = resolveAncestors(canonical, nodesById)
      const { effectiveActive } = computeEffectiveState(node, ancestors)
      if (!effectiveActive) {
        blockers.push({
          code: 'canonical_not_effective_active',
          message: 'Основна категорія недоступна або неактивна',
        })
      }
    }
  }

  taxonomy.catalogTaxonomyAssignmentIds.forEach((id) => {
    if (id === canonical) return
    const node = getNode(nodesById, id)
    if (!node) {
      warnings.push({
        code: 'assignment_missing',
        message: `Додаткову категорію не знайдено (${id}).`,
      })
      return
    }
    const ancestors = resolveAncestors(id, nodesById)
    const { effectiveArchived, effectiveActive } = computeEffectiveState(node, ancestors)
    if (effectiveArchived || !effectiveActive) {
      warnings.push({
        code: 'extra_unavailable',
        message: `Додаткова категорія «${node.name || id}» недоступна або в архіві.`,
      })
    }
  })

  if (!Array.isArray(product?.photos) || product.photos.length === 0) {
    warnings.push({ code: 'missing_photos', message: 'Немає фотографій товару.' })
  }
  if (product?.price == null || product.price === '') {
    warnings.push({ code: 'missing_price', message: 'Немає ціни.' })
  }

  return { ok: blockers.length === 0, blockers, warnings }
}

export { isCatalogTaxonomyLeafNode } from '../components/CatalogTaxonomy/catalogTaxonomyLeaf'

export function buildNormalizedTaxonomyState({
  assignmentIds,
  previousCanonical,
  nodesById,
}) {
  const normalized = normalizeProductTaxonomyAssignments({
    assignmentIds,
    previousCanonical,
  })
  return {
    ...normalized,
    catalogTaxonomyMembershipIds: buildCatalogTaxonomyMembershipIds(
      normalized.catalogTaxonomyAssignmentIds,
      nodesById
    ),
  }
}
